import React, { useState } from "react";
import SlidingToggleSwitch from "@ats/src/components/forms/SlidingToggleSwitch";
import { OptionPair } from "@ats/src/components/forms/SlidingToggleSwitch";

import {
  PricingOptions,
  Badge,
  BillingPeriodToggleWrapper,
  Subtitle,
  PricingOptionsContainer,
  CurrentSubscription,
} from "@ats/src/views/accountAdmin/accountBilling/AccountBillingComponents";
import Icon from "@ats/src/components/shared/Icon";
import ManageBillingActions from "@ats/src/views/accountAdmin/accountBilling/ManageBillingActions";
import PlanCard from "@ats/src/views/accountAdmin/accountBilling/PlanCard";
import {
  getPlansForPeriod,
  getPlanButtonType,
  getUnsubscribedPlanButtonText,
} from "@ats/src/lib/planLookups";
import { useToastContext } from "@shared/context/ToastContext";
import { useModalContext } from "@shared/context/ModalContext";
import {
  useCreateStripeCheckoutSession,
  useCreateStripeCustomerPortalSession,
  useCreateSubscription,
} from "@shared/queryHooks/useBilling";
import { usePlanLimitsGate } from "@shared/hooks/usePlanLimitsGate";
import PlanChangeBlockedModal from "@ats/src/components/modals/PlanChangeBlockedModal";
import ContactUsCallout from "@ats/src/views/accountAdmin/accountBilling/ContactUsCallout";
import AiCreditsCallout from "@ats/src/views/accountAdmin/accountBilling/AiCreditsCallout";
import { FeatureFlipper } from "@ats/src/components/shared/FeatureFlipper";

type AccountBillingPlansUnsubscribedProps = {
  location: any;
  customer: any;
  publishedJobsCount: number;
  canRenewSubscription: boolean;
  billingPrices: any[];
  hasInactiveSubscription: boolean;
  hasNeverSubscribed: boolean;
  history: any;
};

const AccountBillingPlansUnsubscribed: React.FC<AccountBillingPlansUnsubscribedProps> = ({
  location,
  customer,
  publishedJobsCount,
  canRenewSubscription,
  billingPrices,
  hasInactiveSubscription,
  hasNeverSubscribed,
  history,
}) => {
  const addToast = useToastContext();
  const { openModal, removeModal } = useModalContext();

  /* Plan Limits Gate Hook
  --===================================================-- */
  const { checkPlanLimitsGate } = usePlanLimitsGate();

  const billingOptions: OptionPair = [
    {
      label: "Monthly",
      value: "monthly",
    },
    {
      label: "Yearly",
      value: "yearly",
    },
  ];
  const [billingPeriod, setBillingPeriod] = useState<"monthly" | "yearly">(
    billingOptions[0].value as "monthly" | "yearly",
  );

  /* Create Stripe Customer Portal Session
  --===================================================-- */
  const { mutate: createStripeCustomerPortalSession } = useCreateStripeCustomerPortalSession();

  /* Create Stripe Checkout Session
  --===================================================-- */
  const {
    mutate: createStripeCheckoutSession,
    error: errorOnCreateCheckoutSession,
    isLoading: isLoadingCreateCheckoutSession,
  } = useCreateStripeCheckoutSession();

  /* Create Subscription
  --===================================================-- */
  const {
    mutate: createSubscription,
    isLoading: isLoadingCreateSubscription,
  } = useCreateSubscription();

  window.logger(
    "%c[AccountBillingPlansUnsubscribed] render",
    "background: #ed79ed; color: #b5d0eb",
    {
      location,
      customer,
      canRenewSubscription,
      publishedJobsCount,
      billingPrices,
      billingPeriod,
      hasNeverSubscribed,
    },
  );

  /**
   * USED IF A CUSTOMER WANTS TO RENEW A SUBSCRIPTION
   * This function handles the creation of a new subscription for a customer that has already had a Subscription in the past.
   * Upon successful creation of the subscription, it displays a success toast message.
   * If the creation fails, it displays a warning toast message.
   */
  const handleCreateSubscription = (priceId: string) => {
    createSubscription(
      { priceId },
      {
        onSuccess: (data) => {
          window.logger("%c[AccountBilling] createSubscription SUCCESS", "color: #19d219", {
            data,
          });
          addToast({
            title: "Subscription activated",
            kind: "success",
          });
        },
        onError: (response) => {
          window.logger("%c[AccountBilling] createSubscription FAILURE", "color: #990f6b", {
            response,
          });
          addToast({
            title: "Unable to renew subscription",
            kind: "warning",
          });
        },
      },
    );
  };

  /**
   * USED IF A CUSTOMER WANTS TO START A NEW SUBSCRIPTION
   * This function handles the creation of a Stripe Checkout Session for new subscriptions.
   * It checks if a target billing price is defined, then calls the `createStripeCheckoutSession` function
   * with parameters for a subscription session, including the price ID, success and cancel URLs,
   * and the current location pathname. Upon successful creation of the session, it uses the Stripe object
   * to redirect the user to the Stripe Checkout page. If the target billing price is not defined,
   * it displays a warning toast message advising the user to contact support.
   */
  const handleCreateCheckoutSubscriptionSession = ({ priceId, planType, planPeriod }) => {
    createStripeCheckoutSession(
      {
        priceId,
        successUrl: `/hire/settings/billing`,
        cancelUrl: `/hire/settings/billing`,
        locationPathname: location.pathname,
        checkoutMode: "subscription",
        planType,
        planPeriod,
      },
      {
        onSuccess: (data) => {
          window.logger(
            "%c[AccountBilling] completed creation of Subscription Checkout Session",
            "background: orange; color: #1976D2",
            data,
          );
          (window as any).STRIPE.redirectToCheckout({
            sessionId: data.id,
          }).then(function (result) {
            window.logger(
              "%c[AccountBilling] REDIRECTING",
              "background: green; color: white",
              result,
            );
            // If `redirectToCheckout` fails due to a browser or network
            // error, display the localized error message to your customer
            // using `result.error.message`.
          });
        },
        onError: (error: any) => {
          const errorMessage =
            error?.data?.errors?.general?.[0] || "Unable to start checkout session.";
          window.logger(
            "%c[AccountBilling] ERROR creating Custom Stripe Billing Portal session",
            "background: orange; color: white",
            error,
          );
          addToast({ title: errorMessage, kind: "error" });
        },
      },
    );
  };

  /**
   * USED IF A CUSTOMER WANTS TO MANAGE THEIR BILLING USING THE STRIPE CUSTOMER PORTAL
   * This function handles the creation of a Stripe Customer Portal Session.
   * It calls the `createStripeCustomerPortalSession` function with specific parameters,
   * including the return URL. Upon successful creation of the session, it redirects the user
   * to the Stripe Customer Portal page. If the redirection fails, it logs the error message.
   */
  const handleCreateBillingPortalSession = () => {
    createStripeCustomerPortalSession(
      { returnUrl: "/hire/settings/billing" },
      {
        onSuccess: (data) => {
          window.logger("%c[AccountContainer] ", "color: #1976D2", { data });
          window.location.href = data.redirectUrl;
        },
        onError: (error: any) => {
          const errorMessage =
            error?.data?.errors?.general?.[0] || "Unable to access billing portal.";
          window.logger(
            "%c[AccountBilling] ERROR creating Custom Stripe Billing Portal session",
            "background: orange; color: white",
            error,
          );
          addToast({ title: errorMessage, kind: "error" });
        },
      },
    );
  };

  // Get plans for current billing period
  const plans = getPlansForPeriod(billingPeriod, billingPrices);

  // Handle plan selection for unsubscribed users
  const handleCreateNewSubscription = (plan) => {
    const result = checkPlanLimitsGate(plan.lookupKey);

    if (result.shouldProceed) {
      const proceedWithCreation = () => {
        if (canRenewSubscription) {
          window.logger(["handlePlanSelection", "canRenewSubscription"], {
            plan,
            canRenewSubscription,
          });
          handleCreateSubscription(plan.priceId);
        } else {
          window.logger(["handlePlanSelection", "createNewSubscription"], {
            plan,
            canRenewSubscription,
          });
          handleCreateCheckoutSubscriptionSession({
            priceId: plan.priceId,
            planType: "limited",
            planPeriod: billingPeriod,
          });
        }
      };
      proceedWithCreation();
    } else {
      // Show plan limits modal
      const modal = (
        <PlanChangeBlockedModal
          onCancel={() => removeModal()}
          modalData={result.modalData!}
          location={location}
          history={history}
        />
      );
      openModal(modal);
    }
  };

  return (
    <>
      <Subtitle>Subscription options</Subtitle>
      <BillingPeriodToggleWrapper>
        <SlidingToggleSwitch
          name="billingPeriod"
          value={billingPeriod}
          options={billingOptions}
          onChange={(_, value) => setBillingPeriod(value as "monthly" | "yearly")}
        />
        <Badge style={{ marginTop: "16px" }}>2 months free</Badge>
      </BillingPeriodToggleWrapper>

      <PricingOptionsContainer>
        {!hasNeverSubscribed && (
          <CurrentSubscription>
            <p>
              <Icon name="info" />
              <span>You have no active subscriptions</span>
            </p>
            <ManageBillingActions
              hasActiveSubscription={false}
              hasCoupon={false}
            />
          </CurrentSubscription>
        )}

        <PricingOptions>
          {/* Paid Plan Cards */}
          {plans.filter((plan) => plan.key !== "free").map((plan) => {
            const buttonText = getUnsubscribedPlanButtonText(hasNeverSubscribed);
            const buttonType = getPlanButtonType(buttonText);
            const planWithButtonText = {
              ...plan,
              buttonText,
              buttonType,
            };

            // Check if this is the current plan (free plan for unsubscribed users)
            const isCurrentPlan =
              plan.key === "free" && (hasNeverSubscribed || hasInactiveSubscription);

            return (
              <PlanCard
                key={plan.key}
                plan={planWithButtonText}
                billingPeriod={billingPeriod}
                isCurrentPlan={isCurrentPlan} // Use the calculated value
                hasActiveSubscription={false}
                hasCoupon={false}
                isLoading={isLoadingCreateSubscription || isLoadingCreateCheckoutSession}
                isLoadingButton={isLoadingCreateSubscription || isLoadingCreateCheckoutSession}
                onCreateNewSubscription={() => handleCreateNewSubscription(plan)}
              />
            );
          })}
        </PricingOptions>

        <ContactUsCallout />

        <FeatureFlipper feature="AI_APPLICANT_SUMMARY">
          <AiCreditsCallout
            onClickManage={() => history.push("/hire/settings/plato-ai/billing")}
          />
        </FeatureFlipper>
      </PricingOptionsContainer>
    </>
  );
};

export default AccountBillingPlansUnsubscribed;
